import type React from "react"
import { useState, useEffect } from "react"
import { toast } from "sonner"
import { format, parseISO } from "date-fns"
import { Users, X } from "lucide-react"
import { StarRating } from "./star-rating"
import { useAppSelector } from "../../app/hooks"
import type { RootState } from "../../app/store/store"
import {
    createFeedback,
    fetchAnonymousFeedbackInviteStatus,
    fetchFeedbacks,
    updateFeedbackStatus,
    type AnonymousFeedbackInviteStatus,
    type FeedbackItem,
} from "../../features/jobs/jobAPI"
import { ProfileImage } from "../ProfileImage"
import { getMemberRoleLabel } from "../../features/companyMembers/companyMemberAPI"

interface ProfileFeedbackProps {
    jobId?: number | null;
    candidateId?: number | null;
    onFeedbackCountChange?: (count: number) => void;
}

export const ProfileFeedback: React.FC<ProfileFeedbackProps> = ({ jobId, candidateId, onFeedbackCountChange }) => {
    const [comment, setComment] = useState("")
    const [rating, setRating] = useState(0)
    const [showModal, setShowModal] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [isCompleting, setIsCompleting] = useState(false)
    const [feedbacks, setFeedbacks] = useState<FeedbackItem[]>([])
    const [isLoadingFeedbacks, setIsLoadingFeedbacks] = useState(false)
    const [isInviteStatusOpen, setIsInviteStatusOpen] = useState(false)
    const [inviteStatuses, setInviteStatuses] = useState<AnonymousFeedbackInviteStatus[]>([])
    const [isLoadingInviteStatuses, setIsLoadingInviteStatuses] = useState(false)
    const accessToken = useAppSelector((state: RootState) => state.auth.accessToken)
    const userGroups = useAppSelector((state: RootState) => state.auth.user?.groups ?? [])
    const isCompanyHiringManager = userGroups.includes("company_hiring_manager")

    // Fetch feedbacks when component mounts or ids change
    useEffect(() => {
        if (!jobId || !candidateId || !accessToken) return

        const loadFeedbacks = async () => {
            setIsLoadingFeedbacks(true)
            try {
                const result = await fetchFeedbacks(jobId, candidateId, accessToken)
                if (result.error) {
                    console.error("Failed to fetch feedbacks:", result.error)
                    onFeedbackCountChange?.(0)
                } else if (result.data) {
                    setFeedbacks(result.data)
                    onFeedbackCountChange?.(result.data.length)
                } else {
                    onFeedbackCountChange?.(0)
                }
            } catch (error) {
                console.error("Error fetching feedbacks:", error)
            } finally {
                setIsLoadingFeedbacks(false)
            }
        }

        loadFeedbacks()
    }, [jobId, candidateId, accessToken, onFeedbackCountChange])

    // Format date from ISO string
    const formatDate = (dateString: string): string => {
        try {
            const date = parseISO(dateString)
            return format(date, "MMM d, h:mm a")
        } catch (error) {
            return dateString
        }
    }

    // Get full name from company member
    const getFullName = (member: FeedbackItem["company_member"]): string => {
        if (!member) return ""
        const parts = [member.first_name]
        if (member.middle_name) parts.push(member.middle_name)
        if (member.last_name) parts.push(member.last_name)
        return parts.join(" ")
    }

    const getFeedbackAuthorName = (feedback: FeedbackItem): string => {
        return feedback.company_member ? getFullName(feedback.company_member) : feedback.full_name || "Anonymous"
    }

    const getFeedbackAuthorRole = (feedback: FeedbackItem): string => {
        const group = feedback.company_member?.groups?.[0] || feedback.company_member?.group
        return feedback.company_member ? getMemberRoleLabel(group || "") : "Anonymous feedback"
    }

    const getInitials = (name: string): string => {
        const parts = name.trim().split(/\s+/).filter(Boolean)
        if (parts.length === 0) return "?"
        return parts.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join("")
    }

    const handleOpenInviteStatus = async () => {
        if (!jobId || !candidateId || !accessToken) {
            toast.error("Missing required information to load invite statuses.")
            return
        }

        setIsInviteStatusOpen(true)
        setIsLoadingInviteStatuses(true)
        try {
            const result = await fetchAnonymousFeedbackInviteStatus(jobId, candidateId, accessToken)
            if (result.error) {
                toast.error(result.error)
                setInviteStatuses([])
            } else {
                setInviteStatuses(result.data || [])
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : "Failed to load invite statuses."
            toast.error(message)
            setInviteStatuses([])
        } finally {
            setIsLoadingInviteStatuses(false)
        }
    }

    const handleSave = async () => {
        if (!comment.trim() || rating === 0) {
            setShowModal(true)
            // Auto-hide modal after 3 seconds
            setTimeout(() => setShowModal(false), 3000)
            return
        }

        if (!jobId || !candidateId || !accessToken) {
            toast.error("Missing required information to create feedback")
            return
        }

        try {
            setIsSaving(true)
            const payload = {
                job_post: jobId,
                candidate: candidateId,
                comment: comment.trim(),
                rating,
            }

            const result = await createFeedback(payload, accessToken)

            if (result.error) {
                toast.error(result.error)
            } else {
                toast.success("Feedback created successfully")
                // Reset form
                setComment("")
                setRating(0)
                // Reload feedbacks
                if (jobId && candidateId && accessToken) {
                    const reloadResult = await fetchFeedbacks(jobId, candidateId, accessToken)
                    if (reloadResult.data) {
                        setFeedbacks(reloadResult.data)
                        onFeedbackCountChange?.(reloadResult.data.length)
                    } else {
                        onFeedbackCountChange?.(0)
                    }
                }
            }
        } catch (error) {
            console.error("Error creating feedback:", error)
            toast.error("Failed to create feedback. Please try again.")
        } finally {
            setIsSaving(false)
        }
    }

    const completeFeedback = async () => {
        if (!jobId || !candidateId || !accessToken) {
            toast.error("Missing required information to update feedback status")
            return
        }

        try {
            setIsCompleting(true)
            const result = await updateFeedbackStatus(jobId, candidateId, true, accessToken)

            if (result.error) {
                toast.error(result.error)
            } else {
                toast.success(result.data?.message || "Feedback status updated successfully")
            }
        } catch (error) {
            console.error("Error toggling feedback status:", error)
            toast.error("Failed to update feedback status. Please try again.")
        } finally {
            setIsCompleting(false)
        }
    }

    const handleComplete = async () => {
        if (!jobId || !candidateId || !accessToken) {
            toast.error("Missing required information to update feedback status")
            return
        }

        try {
            setIsCompleting(true)
            const result = await fetchAnonymousFeedbackInviteStatus(jobId, candidateId, accessToken)
            if (result.error) {
                toast.error(result.error)
                return
            }

            const statuses = result.data || []
            setInviteStatuses(statuses)
            await completeFeedback()
        } catch (error) {
            const message = error instanceof Error ? error.message : "Failed to check feedback status."
            toast.error(message)
        } finally {
            setIsCompleting(false)
        }
    }

    return (
        <div className="relative space-y-6">
            {/* Add Feedback Form */}
            <div
                className={`rounded-md bg-white p-8 shadow-[0_4px_20px_rgba(0,0,0,0.05)] transition-all duration-300 ${showModal ? "blur-md" : ""}`}
            >
                <div className="mb-6 flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-900">Add Feedback</h2>
                    <StarRating rating={rating} editable onChange={setRating} />
                </div>
                <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Type here...."
                    className="p-2 min-h-[120px] w-full resize-none rounded-md border-t-2 border-black/10 pt-4 text-gray-600 outline-hidden placeholder:text-gray-300"
                />
            </div>

            <div className="relative flex justify-end">
                {showModal && (
                    <>
                        <div
                            className="fixed inset-0 z-40 bg-black/5 backdrop-blur-sm animate-in fade-in duration-300"
                            onClick={() => setShowModal(false)}
                        />
                        <div className="absolute bottom-full right-0 z-50 mb-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                            <div className="relative w-[360px] overflow-hidden rounded-md bg-white p-8 text-center shadow-[0_10px_40px_rgba(0,0,0,0.12)] border border-gray-50">
                                <h3 className="text-lg font-bold leading-snug text-gray-900">
                                    Please complete the feedback to <br /> move forward
                                </h3>
                                {/* Pointer arrow */}
                                <div className="absolute -bottom-2 right-12 h-4 w-4 rotate-45 bg-white border-b border-r border-gray-100/50 shadow-[4px_4px_10px_rgba(0,0,0,0.02)]" />
                            </div>
                        </div>
                    </>
                )}

                <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="btn-cta rounded-md px-16 py-2 font-bold disabled:opacity-60"
                >
                    {isSaving ? "Saving..." : "Save"}
                </button>
            </div>

            {/* Existing Feedback List */}
            <div
                className={`rounded-md bg-white p-8 shadow-[0_4px_20px_rgba(0,0,0,0.05)] transition-all duration-300 ${showModal ? "blur-md" : ""}`}
            >
                <div className="mb-4 flex items-center justify-between gap-4">
                    <h2 className="text-2xl font-bold text-gray-900">Feedback</h2>
                    <button
                        type="button"
                        onClick={handleOpenInviteStatus}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-gray-200 text-gray-500 transition hover:bg-gray-50 hover:text-gray-900"
                        aria-label="View feedback invite statuses"
                        title="View feedback invite statuses"
                    >
                        <Users className="h-5 w-5" />
                    </button>
                </div>
                <hr className="mb-8 border-[1.5px] border-black/10" />

                {isLoadingFeedbacks ? (
                    <div className="py-8 text-center text-gray-500">Loading feedbacks...</div>
                ) : feedbacks.length === 0 ? (
                    <div className="py-8 text-center text-gray-500">No feedbacks yet.</div>
                ) : (
                    <div className="space-y-10">
                        {feedbacks.map((fb) => {
                            const authorName = getFeedbackAuthorName(fb)
                            return (
                                <div key={fb.id} className="flex flex-col gap-2">
                                    <div className="flex items-start justify-between">
                                        <div className="flex flex-col">
                                            <div className="flex gap-3">
                                                <div className="h-10 w-10 rounded-md overflow-hidden flex-shrink-0">
                                                    {fb.company_member ? (
                                                        <ProfileImage
                                                            profilePicture={fb.company_member.profile_picture}
                                                            profilePictureUrl={fb.company_member.profile_picture_url}
                                                            accessToken={accessToken || undefined}
                                                            alt={authorName}
                                                            className="h-10 w-10 rounded-md object-cover"
                                                            fallbackPath="/assets/images/Avatar2.jpg"
                                                        />
                                                        
                                                    ) : (
                                                        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#ABD405] text-sm font-bold text-white">
                                                            {getInitials(authorName)}
                                                        </div>
                                                    )}
                                                </div>
                                            <div>
                                                <h3 className="text-sm font-bold text-[#323C47]">
                                                    {authorName}
                                                </h3>
                                                <p className="text-xs font-medium text-[#323C47]">
                                                    {getFeedbackAuthorRole(fb)}
                                                </p>
                                                <p className="mt-1 text-xs text-gray-500 font-medium">
                                                    {formatDate(fb.created_at)}
                                                </p>
                                            </div>
                                        </div>
                                        <p className="mt-4 text-sm leading-relaxed font-medium text-gray-700">
                                            {fb.comment}
                                        </p>
                                    </div>
                                    <div className="pt-7 shrink-0">
                                        <StarRating rating={fb.rating} />
                                    </div>
                                </div>
                            </div>
                            )
                        })}
                    </div>
                )}
            </div>

            {isInviteStatusOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                    onClick={() => setIsInviteStatusOpen(false)}
                >
                    <div
                        className="relative w-full max-w-lg rounded-md bg-white p-6 shadow-xl"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <button
                            type="button"
                            onClick={() => setIsInviteStatusOpen(false)}
                            aria-label="Close invite statuses"
                            className="absolute right-4 top-4 rounded-md p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
                        >
                            <X className="h-5 w-5" />
                        </button>

                        <h3 className="mb-1 text-xl font-bold text-gray-900">Feedback invite status</h3>
                        <p className="mb-6 text-sm text-gray-500">
                            People invited to give feedback.
                        </p>

                        {isLoadingInviteStatuses ? (
                            <div className="py-8 text-center text-sm text-gray-500">Loading invite statuses...</div>
                        ) : inviteStatuses.length === 0 ? (
                            <div className="py-8 text-center text-sm text-gray-500">No feedback invites found.</div>
                        ) : (
                            <div className="max-h-[420px] space-y-3 overflow-y-auto pr-1">
                                {inviteStatuses.map((invite) => (
                                    <div
                                        key={invite.id}
                                        className="flex items-start justify-between gap-4 rounded-md border border-gray-100 bg-gray-50 p-4"
                                    >
                                        <div className="min-w-0">
                                            <p className="truncate text-sm font-bold text-gray-900">
                                                {invite.recipient_email}
                                            </p>
                                            <p className="mt-1 text-xs text-gray-500">
                                                Invited {formatDate(invite.created_at)}
                                            </p>
                                            {invite.completed_at && (
                                                <p className="mt-1 text-xs text-gray-500">
                                                    Completed {formatDate(invite.completed_at)}
                                                </p>
                                            )}
                                        </div>
                                        <span
                                            className={`shrink-0 rounded-md px-3 py-1 text-xs font-bold ${
                                                invite.has_completed_feedback
                                                    ? "bg-green-100 text-green-700"
                                                    : "bg-yellow-100 text-yellow-700"
                                            }`}
                                        >
                                            {invite.has_completed_feedback ? "Given" : "Pending"}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {isCompanyHiringManager && (
                <div className="flex justify-end">
                    <button
                        onClick={handleComplete}
                        disabled={isCompleting}
                        className="btn-cta rounded-md px-16 py-2 font-bold disabled:opacity-60"
                    >
                        {isCompleting ? "Completing..." : "Complete"}
                    </button>
                </div>
            )}
        </div>
    )
}
